// Libraries
import React from 'react';

// Supermove
import {Space, Styled} from '@supermove/components';
import {gql} from '@supermove/graphql';
import {useDrawer} from '@supermove/hooks';
import {ProjectModel} from '@supermove/models';
import {Currency} from '@supermove/utils';

// App
import FieldValue from '@shared/design/components/Field/FieldValue';
import EditValuationCoverageDrawer from '@shared/modules/Project/components/EditValuationCoverageDrawer';
import ProjectDetailsBlockHeader, {
  BlockHeaderLoader,
  EditButton,
} from '@shared/modules/Project/components/widgets/components/ProjectDetailsBlockHeader';
import ProjectDetailsBlockWrapper from '@shared/modules/Project/components/widgets/components/ProjectDetailsBlockWrapper';

const Column = Styled.View`
`;

const SkeletonComponent = () => {
  return (
    <React.Fragment>
      <BlockHeaderLoader
        title={'Valuation Coverage'}
        ActionsComponent={() => <EditButton isDisabled />}
      />
      <FieldValue.SkeletonLoader label={'Selected Option'} width={144} />
      <Space height={16} />
      <FieldValue.SkeletonLoader label={'Selected Value'} width={144} />
      <Space height={16} />
      <FieldValue.SkeletonLoader label={'Minimum'} width={144} />
    </React.Fragment>
  );
};

const EditValuationCoverageButton = ({
  project,
  refetch,
}: {
  project: ProjectModel;
  refetch: () => void;
}) => {
  const editValuationCoverageDrawer = useDrawer({name: 'Edit Valuation Coverage Drawer'});
  return (
    <React.Fragment>
      <EditButton onPress={editValuationCoverageDrawer.handleOpen} />
      <EditValuationCoverageDrawer
        key={editValuationCoverageDrawer.key}
        isOpen={editValuationCoverageDrawer.isOpen}
        handleClose={editValuationCoverageDrawer.handleClose}
        projectUuid={project.uuid}
        onSuccess={refetch}
      />
    </React.Fragment>
  );
};

const ValuationCoverageFields = ({project}: {project: ProjectModel}) => {
  const {valuationCoverage} = project;
  return (
    <Column>
      <FieldValue value={valuationCoverage?.displayName} label={'Selected Option'} isResponsive />
      <Space height={16} />
      <FieldValue
        value={valuationCoverage ? Currency.display(valuationCoverage.amountValue) : undefined}
        label={'Selected Value'}
        isResponsive
      />
      <Space height={16} />
      <FieldValue
        value={valuationCoverage ? Currency.display(valuationCoverage.amountMinimum) : undefined}
        label={'Minimum'}
        isResponsive
      />
    </Column>
  );
};

const ProjectValuationCoverageBlockContent = ({
  project,
  refetch,
}: {
  project: ProjectModel;
  refetch: () => void;
}) => {
  return (
    <React.Fragment>
      <ProjectDetailsBlockHeader
        title={`Valuation Coverage`}
        ActionsComponent={EditValuationCoverageButton}
        actionsComponentProps={{project, refetch}}
      />
      <ValuationCoverageFields project={project} />
    </React.Fragment>
  );
};

const ProjectValuationCoverageBlock = ({
  projectUuid,
  index,
}: {
  projectUuid: string;
  index: number;
}) => {
  return (
    <ProjectDetailsBlockWrapper
      index={index}
      query={ProjectValuationCoverageBlock.query}
      queryVariables={{projectUuid}}
      SkeletonComponent={() => <SkeletonComponent />}
    >
      {({data, refetch}: {data: {project: ProjectModel}; refetch: () => void}) => {
        return <ProjectValuationCoverageBlockContent project={data.project} refetch={refetch} />;
      }}
    </ProjectDetailsBlockWrapper>
  );
};

// --------------------------------------------------
// Data
// --------------------------------------------------
ProjectValuationCoverageBlock.query = gql`
  query ProjectValuationCoverageBlock($projectUuid: String!) {
    ${gql.query}
    project(uuid: $projectUuid) {
      id
      uuid
      valuationCoverage {
        id
        amountMinimum
        amountValue
        displayName
      }
    }
  }
`;

export default ProjectValuationCoverageBlock;
